import { zodResolver } from '@hookform/resolvers/zod'
import { TYPE_ACCOUNT } from '@idc-forms/schemas'
import {
  type V2CreateAdminUserInput,
  V2CreateAdminUserSchema
} from '@idc-forms/schemas/api-v2/admin-users'
import { Controller, useForm } from 'react-hook-form'
import type { z } from 'zod'
import { useRolesV2LookupQuery } from '@/features/roles/hooks/use-roles-v2-lookup-query'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import { Button } from '@/shared/ui/button'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet
} from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { MultiSelect } from '@/shared/ui/multi-select'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Switch } from '@/shared/ui/switch'
import { TYPE_ACCOUNT_LABELS } from './users-admin-filter.registry'

/** Valores del form = INPUT del schema (roleIds como strings antes del brand). */
type UserV2FormValues = z.input<typeof V2CreateAdminUserSchema>

type UserV2FormProps = {
  /** Valores iniciales (edición) — ausente en creación. */
  defaultValues?: Partial<UserV2FormValues>
  submitLabel: string
  pending?: boolean
  /** En creación se muestra la nota de contraseña por correo. */
  isCreate?: boolean
  onSubmit: (values: V2CreateAdminUserInput) => void
  onCancel: () => void
}

/**
 * Formulario de usuario v2 (crear/editar) para el side panel. RHF + zodResolver
 * con el MISMO schema del contrato v2 (`V2CreateAdminUserSchema`): roles por id
 * público (`rol_`), nombres wire (`fullName`/`accountType`). Sin credenciales: la
 * contraseña inicial la genera el backend y viaja por correo.
 */
export function UserV2Form({
  defaultValues,
  submitLabel,
  pending = false,
  isCreate = false,
  onSubmit,
  onCancel
}: UserV2FormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<UserV2FormValues, unknown, V2CreateAdminUserInput>({
    resolver: zodResolver(V2CreateAdminUserSchema),
    defaultValues: {
      accountType: TYPE_ACCOUNT.USER,
      roleIds: [],
      active: true,
      ...defaultValues
    }
  })

  const { data: roles = [] } = useRolesV2LookupQuery()
  const roleOptions = roles.map((role) => ({ value: role.id, label: role.name }))

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 px-3 py-3" noValidate>
      <FieldSet className="gap-4">
        <FieldLegend>Identificación</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="fullName"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.fullName)}>
                <FieldLabel htmlFor="user-v2-fullname">Nombre completo</FieldLabel>
                <Input id="user-v2-fullname" value={field.value ?? ''} onChange={field.onChange} />
                <FieldError errors={[errors.fullName]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="username"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.username)}>
                <FieldLabel htmlFor="user-v2-username">Usuario</FieldLabel>
                <Input
                  id="user-v2-username"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldDescription>Solo letras y números, sin espacios.</FieldDescription>
                <FieldError errors={[errors.username]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.email)}>
                <FieldLabel htmlFor="user-v2-email">Correo</FieldLabel>
                <Input
                  id="user-v2-email"
                  type="email"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                {isCreate && (
                  <FieldDescription>
                    La contraseña inicial se genera automáticamente y se envía a este correo.
                  </FieldDescription>
                )}
                <FieldError errors={[errors.email]} />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Cuenta y permisos</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="accountType"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.accountType)}>
                <FieldLabel htmlFor="user-v2-account-type">Tipo de cuenta</FieldLabel>
                <NativeSelect
                  id="user-v2-account-type"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                >
                  {Object.values(TYPE_ACCOUNT).map((value) => (
                    <NativeSelectOption key={value} value={value}>
                      {TYPE_ACCOUNT_LABELS[value]}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldError errors={[errors.accountType]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="roleIds"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.roleIds)}>
                <FieldLabel htmlFor="user-v2-roles">Roles</FieldLabel>
                <MultiSelect
                  id="user-v2-roles"
                  options={roleOptions}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  placeholder="Selecciona roles"
                  searchPlaceholder="Buscar rol..."
                  emptyText="Sin roles"
                />
                <FieldDescription>
                  Los administradores tienen todos los permisos sin importar los roles.
                </FieldDescription>
              </Field>
            )}
          />

          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="user-v2-active">Activo</FieldLabel>
                <Switch
                  id="user-v2-active"
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <SidePanelActions
        actions={[
          {
            label: submitLabel,
            variant: 'default',
            disabled: pending,
            primary: true,
            onClick: () => {
              void handleSubmit(onSubmit)()
            }
          },
          {
            label: 'Cancelar',
            variant: 'outline',
            disabled: pending,
            onClick: onCancel
          }
        ]}
      />
      {/* El submit real va por SidePanelActions; este botón invisible habilita Enter. */}
      <Button type="submit" className="hidden" disabled={pending}>
        {submitLabel}
      </Button>
    </form>
  )
}
